import React, { useMemo, useCallback, Fragment, useState, useEffect } from 'react'
import { useTable, usePagination } from 'react-table'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import Pagination from 'Design/components/Pagination'
import ConfirmationModal from 'Design/components/ConfirmationModal'
import Table, { TableActions, TableBody, TableHead, TableRow, Td, Th } from 'Design/components/Table'
import { useGetBotProfileQuery, useUpdateBotProfileMutation } from 'Services/bot-profiles'
import PageLoader from 'Layout/PageLoader'
import parseErrorMessage from 'Utils/error-message-parser'
import * as date from 'Utils/date'
import _ from 'lodash'
import cx from 'classnames'

// assets
import BotIconSvg from 'Images/bot-icon.svg'
import InformationLineSvg from 'Images/information-line.svg'

// others
import BotOptions from './BotOptions'
import EditFlowModal from '../../../../containers/flow/EditFlowModal'
import { useGetBotFlowsQuery, useUpdateBotMutation, useDeleteBotMutation } from './bot-flows.service'
import ReactToolTip from '../../../components/ReactTooltip/ReactTooltip'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 15

const DefaultBotBadge = () => {
  return (
    <div className='default-bot-icon'>
      <BotIconSvg width='1em' height='1em' />
      <span> Default Bot </span>
      <InformationLineSvg width='1em' height='1em' />
    </div>
  )
}

const getDefaultParams = () => {
  const params = new URLSearchParams(location.search)

  const paramsSize = params.get('size') || DEFAULT_PAGE_SIZE
  const paramsPage = params.get('page') || DEFAULT_PAGE

  const page = isNaN(paramsPage) ? DEFAULT_PAGE : parseInt(paramsPage)
  const size = isNaN(paramsSize) ? DEFAULT_PAGE_SIZE : parseInt(paramsSize)

  return {
    page,
    size
  }
}

const getInitialApiSearchParams = (params) => {
  return {
    offset: (params.page - 1) * params.size,
    limit: params.size
  }
}

const BotsList = (props) => {
  const { botId, container } = props

  const [, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const initialParams = useMemo(getDefaultParams, [])

  const [queryParams, setQueryParams] = useState(getInitialApiSearchParams(initialParams))
  const [pageSize] = useState(initialParams.size)
  const [showEditModal, setShowEditModal] = useState(false)
  const [activeFlowId, setActiveFlowId] = useState(null)
  const [flowSettings, setFlowSettings] = useState({})
  const [selectedBotData, setSelectedBotData] = useState(null)
  const [showDeleteBotModal, setShowDeleteBotModal] = useState(false)

  const {
    isLoading: isFetchingBotProfiles,
    data = {},
    isFetching
  } = useGetBotFlowsQuery({ botId, params: queryParams }, { refetchOnMountOrArgChange: true })
  const { results: botFlows = [], meta: paginationDetails = {} } = data

  const { data: bot } = useGetBotProfileQuery(botId)

  const [updateBot] = useUpdateBotMutation()
  const [deleteBot, deleteBotResult] = useDeleteBotMutation()
  const [updateBotProfile] = useUpdateBotProfileMutation()

  const debouncedSetQueryParams = useMemo(() => {
    return _.debounce(setQueryParams, 500, { leading: true, trailing: true })
  }, [])

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'settings.name'
      },
      {
        id: 'created_at',
        Header: 'Created on',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          return date.formatRelativeDate(value.created_at, 7)
        },
        Tooltip: ({ cell: { value } }) => {
          return date.formatLongDateTime(value.created_at)
        }
      },
      {
        id: 'updated_at',
        Header: 'Last updated on',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          return date.formatRelativeDate(value.updated_at || value.created_at, 7)
        },
        Tooltip: ({ cell: { value } }) => {
          return date.formatLongDateTime(value.updated_at || value.created_at)
        }
      }
    ]
  }, [])

  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data: botFlows,
      initialState: {
        pageIndex: initialParams.page - 1,
        pageSize
      },
      // pagination
      manualPagination: true,
      pageCount: Math.ceil(paginationDetails.total / pageSize)
    },
    usePagination
  )

  const handleRowClick = useCallback(
    (flowId) => {
      navigate(`${botId}/flows/${flowId}`)
    },
    [navigate, botId]
  )

  const handleEditBot = useCallback(
    (id) => {
      setActiveFlowId(id)
      const flowSettings = botFlows.find((flow) => flow.id === id)?.settings || {}
      setFlowSettings(flowSettings)
      setShowEditModal(true)
    },
    [botFlows]
  )

  const onDeleteBot = async (flowId) => {
    const promise = deleteBot({ botId, flowId }).unwrap()

    toast.promise(promise, {
      loading: 'Deleting Bot',
      success: 'Bot Deleted successfully',
      error: parseErrorMessage('Unable to delete bot. Try again...')
    })

    promise.then(handleCloseDeleteModal)
  }

  const handleSetDefaultBot = useCallback(
    (flowId) => {
      const { display_name, embed_token, id, name, settings, status, widget_type } = bot
      const flow = botFlows.find((flow) => flow.id === flowId)
      const payload = {
        display_name,
        embed_token,
        id,
        name,
        settings,
        status,
        widget_type,
        default_flow_id: flow.id
      }
      const promise = updateBotProfile(payload).unwrap()
      toast.promise(promise, {
        loading: 'Updating Default Bot',
        success: 'Updated Default Bot',
        error: 'Error Updating Default Bot'
      })
    },
    [bot, botFlows, updateBotProfile]
  )

  const handleTestBot = (flowId) => {
    window.open(`/test/${bot.embed_token}?flow_id=${flowId}`, '_blank')
  }

  const hideEditModal = () => {
    setActiveFlowId(null)
    setFlowSettings({})
    setShowEditModal(false)
  }

  const onEditBotFlowSettings = async (payload) => {
    const promise = updateBot(payload).unwrap()
    toast.promise(promise, {
      loading: 'Updating Bot',
      success: 'Updated Bot',
      error: 'Error Updating Bot'
    })
  }

  const handleOpenDeleteModal = () => {
    setShowDeleteBotModal(true)
  }

  const handleCloseDeleteModal = () => {
    setShowDeleteBotModal(false)
  }

  const handleDeleteBot = () => {
    onDeleteBot(selectedBotData.id)
  }

  useEffect(() => {
    const params = {}

    const apiParams = {
      offset: pageIndex * pageSize,
      limit: pageSize,
      ...params
    }

    const urlPrams = {
      page: pageIndex + 1,
      size: pageSize,
      ...params
    }

    debouncedSetQueryParams(apiParams)
    setSearchParams(urlPrams, { replace: true })
  }, [pageIndex, pageSize, debouncedSetQueryParams, setSearchParams])

  if (isFetchingBotProfiles || isFetching) {
    return <PageLoader />
  }

  const isDeleting = deleteBotResult.isLoading

  return (
    <Fragment>
      <ConfirmationModal
        isOpen={showDeleteBotModal}
        title='Delete Bot'
        onCloseButtonClick={handleCloseDeleteModal}
        onConfirmButtonClick={handleDeleteBot}
        confirmButtonText='Yes'
        cancelButtonText='No'
        disableConfirmButton={isDeleting}
      >
        Are you sure you want to delete the bot <strong>{selectedBotData?.settings?.name}</strong>?
      </ConfirmationModal>
      {showEditModal && (
        <EditFlowModal
          isOpen
          hide={hideEditModal}
          botId={botId}
          flowId={activeFlowId}
          settings={flowSettings}
          onEditBotFlowSettings={onEditBotFlowSettings}
          container={container}
        />
      )}
      <TableActions>
        <Pagination
          currentPage={pageIndex + 1}
          pageSize={pageSize}
          totalItems={paginationDetails.total}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableActions>
      <Table {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup) => {
            const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps()

            return (
              <TableRow key={key} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key, ...headerProps } = column.getHeaderProps()

                  return (
                    <Th key={key} {...headerProps}>
                      {column.render('Header')}
                    </Th>
                  )
                })}
                <Th isOption />
              </TableRow>
            )
          })}
        </TableHead>

        <TableBody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            const { key, ...rowProps } = row.getRowProps()
            return (
              <TableRow key={key} {...rowProps} clickable>
                {row.cells.map((cell) => {
                  const { key, ...cellProps } = cell.getCellProps()
                  const isDateColumn = key.includes('created_at') || key.includes('updated_at')
                  return (
                    <Td key={key} {...cellProps} onClick={() => handleRowClick(row.original.id)}>
                      {isDateColumn && (
                        <div className='date-cell-container'>
                          <ReactToolTip
                            className='bot-date-tooltip'
                            side='top'
                            content={
                              <div className='bot-date-tooltip-content' onClick={(e) => e.stopPropagation()}>
                                {cell.render('Tooltip')}
                              </div>
                            }
                          >
                            {cell.render('Cell')}
                          </ReactToolTip>
                        </div>
                      )}
                      {!isDateColumn && (
                        <div
                          className={cx('default-bot-container', {
                            'highlight-font-weight':
                              row.original.id === bot?.default_flow_id && key.includes('settings.name')
                          })}
                        >
                          {cell.render('Cell')}
                          {row.original.id === bot?.default_flow_id && key.includes('settings.name') && (
                            <ReactToolTip
                              className='bot-list-tooltip'
                              side='right'
                              content={
                                <div className='bot-list-tooltip-content' onClick={(e) => e.stopPropagation()}>
                                  Visitors are shown the opening step of the Default Bot when starting a new
                                  conversation
                                </div>
                              }
                            >
                              <DefaultBotBadge />
                            </ReactToolTip>
                          )}
                        </div>
                      )}
                    </Td>
                  )
                })}
                <Td onClick={() => setSelectedBotData(row.original)}>
                  <BotOptions
                    id={row.original.id}
                    isDefaultBot={row.original.id === bot?.default_flow_id}
                    onEditBot={handleEditBot}
                    onTestBot={handleTestBot}
                    onDeleteBot={handleOpenDeleteModal}
                    onSetDefaultBot={handleSetDefaultBot}
                  />
                </Td>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Fragment>
  )
}

export default BotsList
